import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Filter, Eye, Pencil, MoreVertical, KeyRound, ShieldOff, UserCheck, UserX } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { PaginationBar, EmptyState, LoadingBlock } from "@/components/Pagination";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";
import { ROLE_LABELS } from "@/constants/roles";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PAGE_SIZE = 15;
const STATUSES = ["active", "inactive", "suspended", "pending_verification"];
const ROLES = ["super_admin", "hospital_admin", "faculty", "department_head", "student"];

export default function AdminUsersListPage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [organizationId, setOrganizationId] = useState("all");
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null); // {user, nextStatus, title, ...}

  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (role !== "all") params.role = role;
      if (status !== "all") params.status = status;
      if (organizationId !== "all") params.organization_id = organizationId;
      const { data } = await api.get("/admin/users", { params });
      setUsers(data.data.users);
      setTotal(data.data.total);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [page, search, role, status, organizationId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/organizations");
        setOrgs(data.data.organizations);
      } catch { /* silent */ }
    })();
  }, []);

  const clearFilters = () => {
    setSearch(""); setRole("all"); setStatus("all"); setOrganizationId("all"); setPage(1);
  };

  const applyStatusChange = async () => {
    if (!confirm) return;
    try {
      await api.patch(`/admin/users/${confirm.user.id}/status`, { account_status: confirm.nextStatus });
      toast.success(`Account status updated to ${confirm.nextStatus.replace(/_/g, " ")}.`);
      setConfirm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const activeFilters = useMemo(() => {
    return [role, status, organizationId, search].filter((v) => v && v !== "all").length;
  }, [role, status, organizationId, search]);

  return (
    <DashboardLayout title="Users" breadcrumbs={["Platform", "User Management"]}>
      <div className="space-y-4" data-testid={ADMIN.usersListRoot}>
        {/* Filter bar */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                className="pl-9"
                placeholder="Search by name, email, mobile or employee ID"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                data-testid={ADMIN.usersSearch}
              />
            </div>
            <Select value={role} onValueChange={(v) => { setRole(v); setPage(1); }}>
              <SelectTrigger className="w-40" data-testid={ADMIN.usersRoleFilter}><SelectValue placeholder="Role" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
              <SelectTrigger className="w-40" data-testid={ADMIN.usersStatusFilter}><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={organizationId} onValueChange={(v) => { setOrganizationId(v); setPage(1); }}>
              <SelectTrigger className="w-56" data-testid={ADMIN.usersOrgFilter}><SelectValue placeholder="Organization" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All organizations</SelectItem>
                {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.organization_name}</SelectItem>)}
              </SelectContent>
            </Select>
            {activeFilters > 0 && (
              <button className="text-xs font-semibold text-slate-500 hover:text-[#101876]" onClick={clearFilters}>
                Clear filters
              </button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => navigate("/app/super-admin/users/new")}
                className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c]"
                data-testid={ADMIN.usersAddBtn}
              >
                <Plus className="h-4 w-4" /> Add user
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-slate-200 bg-white">
          {loading ? (
            <LoadingBlock label="Loading users…" />
          ) : users.length === 0 ? (
            <EmptyState title="No users match those filters" description="Try clearing filters or searching differently." icon={Filter} />
          ) : (
            <Table data-testid={ADMIN.usersTable}>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">User</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Role</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Organization</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Designation</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Verified</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Last login</TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={ADMIN.usersRow(u.id)}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#101876] text-xs font-bold text-white">
                          {(u.first_name?.[0] || "") + (u.last_name?.[0] || "")}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{u.full_name}</div>
                          <div className="text-xs text-slate-500 truncate">{u.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{u.role_name || ROLE_LABELS[u.role_code]}</TableCell>
                    <TableCell className="text-sm text-slate-700">{u.organization_name || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-700">{u.designation || "—"}</TableCell>
                    <TableCell><StatusBadge status={u.account_status} /></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-xs">
                        <span className={`h-2 w-2 rounded-full ${u.email_verified ? "bg-emerald-500" : "bg-slate-300"}`} />
                        <span className="text-slate-600">Email</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "Never"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link to={`/app/super-admin/users/${u.id}`} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-[#101876]" data-testid={ADMIN.usersRowView(u.id)}>
                          <Eye className="h-4 w-4" />
                        </Link>
                        <Link to={`/app/super-admin/users/${u.id}/edit`} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-[#101876]" data-testid={ADMIN.usersRowEdit(u.id)}>
                          <Pencil className="h-4 w-4" />
                        </Link>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-[#101876]" data-testid={ADMIN.usersRowMore(u.id)}>
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuLabel className="text-xs text-slate-500">Manage account</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {u.account_status !== "active" && (
                              <DropdownMenuItem onClick={() => setConfirm({ user: u, nextStatus: "active", title: `Activate ${u.full_name}?`, destructive: false, confirmLabel: "Activate" })}>
                                <UserCheck className="mr-2 h-4 w-4" /> Activate
                              </DropdownMenuItem>
                            )}
                            {u.account_status !== "inactive" && (
                              <DropdownMenuItem onClick={() => setConfirm({ user: u, nextStatus: "inactive", title: `Deactivate ${u.full_name}?`, destructive: true, confirmLabel: "Deactivate" })}>
                                <UserX className="mr-2 h-4 w-4" /> Deactivate
                              </DropdownMenuItem>
                            )}
                            {u.account_status !== "suspended" && (
                              <DropdownMenuItem onClick={() => setConfirm({ user: u, nextStatus: "suspended", title: `Suspend ${u.full_name}?`, destructive: true, confirmLabel: "Suspend" })}>
                                <ShieldOff className="mr-2 h-4 w-4" /> Suspend
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem asChild>
                              <Link to={`/app/super-admin/users/${u.id}?reset=1`}><KeyRound className="mr-2 h-4 w-4" /> Reset password</Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={async () => {
                                try {
                                  await api.post("/auth/admin/unlock-user", { email: u.email });
                                  toast.success(`${u.full_name} can sign in again — lockout cleared.`);
                                } catch (err) {
                                  toast.error(formatApiError(err));
                                }
                              }}
                              data-testid={`admin-unlock-${u.id}`}
                            >
                              <UserCheck className="mr-2 h-4 w-4" /> Unlock login (clear lockout)
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <PaginationBar page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testKey="users-pagination" />
        </div>
      </div>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.title}
        description={`This action will change the account status to ${confirm?.nextStatus?.replace(/_/g, " ")}.`}
        confirmLabel={confirm?.confirmLabel}
        destructive={confirm?.destructive}
        onConfirm={applyStatusChange}
        testKey="user-status-confirm"
      />
    </DashboardLayout>
  );
}
